import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { usePermissions } from "@/contexts/PermissionContext";
import { useVisibleRefresh } from "@/hooks/useVisibleRefresh";
import { toast } from "sonner";
import { useDoxaConfirm } from "@/components/doxa/DoxaConfirmDialog";
import {
  Archive,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  Clock3,
  ExternalLink,
  Eye,
  EyeOff,
  FileText,
  Loader2,
  MoreHorizontal,
  Package,
  Paperclip,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Trash2,
  Upload,
  Users,
  X,
} from "lucide-react";
import { useBusyKeys } from "@/hooks/useBusyKeys";
import { RelationSelects, isScheduled, fmtDateTime, norm } from "@/components/RelationSelects";
import DoxaSelect from "@/components/doxa/DoxaSelect";
import { EmptyState, PageHeader, PageLoading } from "@/components/doxa/AdminSurface";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const STATUS_OPTS = [
  { value: "active", label: "Ativos" },
  { value: "archived", label: "Arquivados" },
  { value: "all", label: "Todos" },
];

const MATERIAL_AUDIENCE_LABEL = {
  CLASS: "Toda a turma",
  CELL: "Célula específica",
  STUDENTS: "Alunos específicos",
};

const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;
const MATERIAL_FILE_ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.webp";
const MATERIAL_FILE_EXTENSIONS = new Set(MATERIAL_FILE_ACCEPT.split(","));

function materialAudienceText(material) {
  const audienceType = material.audience_type || "CLASS";
  if (audienceType === "CELL") return `Célula: ${material.audience_cell || "—"}`;
  if (audienceType === "STUDENTS") return `${(material.audience_student_ids || []).length} aluno(s) específico(s)`;
  return MATERIAL_AUDIENCE_LABEL.CLASS;
}

function toLocalInput(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function formatFileSize(bytes) {
  const size = Number(bytes || 0);
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function fileExtension(name) {
  const value = String(name || "").toLowerCase();
  const index = value.lastIndexOf(".");
  return index >= 0 ? value.slice(index) : "";
}

function validateFileForUpload(file) {
  if (!file) return "Selecione um arquivo.";
  if (file.size > MAX_UPLOAD_BYTES) return "O arquivo excede o limite de 50 MB.";
  if (!MATERIAL_FILE_EXTENSIONS.has(fileExtension(file.name))) {
    return "Formato não permitido. Use PDF, Word, Excel, PowerPoint, JPG, PNG ou WebP.";
  }
  return null;
}

function MaterialChip({ icon: Icon, children, tone = "neutral" }) {
  const tones = {
    neutral: "border-[#E7DFD3] bg-[#F6F3EE] text-[#5F574D]",
    orange: "border-[#FFD8C8] bg-[#FFF0E9] text-[#D94A12]",
    success: "border-[#CFE8D8] bg-[#EEF8F2] text-[#2F7A45]",
    warning: "border-[#F3D9A7] bg-[#FFF7E8] text-[#9A6518]",
  };

  return (
    <span className={`inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[11px] font-semibold ${tones[tone] || tones.neutral}`}>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {children}
    </span>
  );
}

function MaterialRowMenu({ material, canManage, busy, onEdit, onArchive, onReactivate }) {
  if (!canManage) return null;
  const archived = material.status === "ARCHIVED";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-transparent text-[#8A8378] transition hover:border-[#F75A1B]/20 hover:bg-[#FFF0E9] hover:text-[#F75A1B] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/25 data-[state=open]:border-[#F75A1B]/20 data-[state=open]:bg-[#FFF0E9] data-[state=open]:text-[#F75A1B]"
          aria-label={`Ações do material ${material.title}`}
          data-testid={`material-row-menu-${material.id}`}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={7} className="w-60 rounded-xl border-[#E0D9CD] bg-white p-1.5 shadow-[0_14px_40px_rgba(45,31,20,.14)]">
        <DropdownMenuLabel className="px-2.5 pb-1 pt-2">
          <span className="block text-[10px] font-semibold uppercase tracking-[0.12em] text-[#A59C90]">Ações do material</span>
          <span className="mt-0.5 block truncate text-xs font-semibold normal-case tracking-normal text-[#4A4640]">{material.title}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#EBE5DB]" />
        <DropdownMenuItem onSelect={onEdit} className="cursor-pointer rounded-lg px-2.5 py-2.5 focus:bg-[#FFF0E9] focus:text-[#D94A12]" data-testid={`edit-material-${material.id}`}>
          <Pencil className="h-4 w-4" /> Editar material
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-[#EBE5DB]" />
        {archived ? (
          <DropdownMenuItem
            disabled={busy}
            onSelect={onReactivate}
            className="cursor-pointer rounded-lg px-2.5 py-2.5 text-[#2F7A45] focus:bg-[#EEF8F2] focus:text-[#2F7A45]"
            data-testid={`reactivate-material-${material.id}`}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Reativar material
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            disabled={busy}
            onSelect={onArchive}
            className="cursor-pointer rounded-lg px-2.5 py-2.5 text-[#8A5A16] focus:bg-[#FFF7E8] focus:text-[#8A5A16]"
            data-testid={`archive-material-${material.id}`}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />} Arquivar material
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Materials() {
  const confirmAction = useDoxaConfirm();
  const { can } = usePermissions();
  const canManage = can("content.materials");
  const { isBusy, run } = useBusyKeys();
  const [items, setItems] = useState([]);
  const [classGroups, setClassGroups] = useState([]);
  const [status, setStatus] = useState("active");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    return api.get("/materials", { params: { status_filter: status } })
      .then(({ data }) => setItems(data))
      .catch((error) => toast.error(formatApiError(error)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { void load(); }, [status]);
  useVisibleRefresh(() => load(), { intervalMs: 0 });
  useEffect(() => {
    api.get("/class-groups")
      .then(({ data }) => setClassGroups(data))
      .catch(() => setClassGroups([]));
  }, []);

  const classGroupMap = Object.fromEntries(classGroups.map((classGroup) => [classGroup.id, classGroup.name]));
  const filtered = items.filter((material) => {
    if (!q) return true;
    return norm([
      material.title,
      material.description,
      classGroupMap[material.class_group_id],
      materialAudienceText(material),
    ].join(" ")).includes(norm(q));
  });

  const archive = (material) => confirmAction({
    title: "Arquivar material?",
    description: <>O material <strong className="font-semibold text-[#1F1F1F]">{material.title}</strong> sairá da visão de materiais ativos. O conteúdo e os vínculos existentes serão preservados.</>,
    confirmLabel: "Arquivar",
    loadingLabel: "Arquivando…",
    variant: "warning",
    dataTestId: `confirm-archive-material-${material.id}`,
    onConfirm: async () => {
      await api.post(`/materials/${material.id}/archive`);
      toast.success("Material arquivado");
      await load();
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  const reactivate = (material) => confirmAction({
    title: "Reativar material?",
    description: <>O material <strong className="font-semibold text-[#1F1F1F]">{material.title}</strong> voltará para a visão de materiais ativos com os vínculos atuais.</>,
    confirmLabel: "Reativar",
    loadingLabel: "Reativando…",
    variant: "positive",
    dataTestId: `confirm-reactivate-material-${material.id}`,
    onConfirm: async () => {
      await api.post(`/materials/${material.id}/reactivate`);
      toast.success("Material reativado");
      await load();
    },
    onError: (error) => toast.error(formatApiError(error)),
  });

  const statusLabel = STATUS_OPTS.find((option) => option.value === status)?.label?.toLowerCase() || "na visão atual";

  if (loading && items.length === 0) return <PageLoading label="Carregando materiais…" />;

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4 sm:p-6 lg:p-8" data-testid="materials-page">
      <PageHeader
        eyebrow="Conteúdo acadêmico"
        title="Materiais"
        description="Organize conteúdos online e materiais físicos com público, disponibilidade e recebimento sob controle."
        meta={`${items.length} material(is) ${statusLabel}`}
        action={canManage ? (
          <button type="button" onClick={() => setEditing("new")} className="doxa-btn-primary inline-flex items-center gap-2 px-4 py-2.5" data-testid="new-material-btn">
            <Plus className="h-4 w-4" /> Novo material
          </button>
        ) : null}
      />

      <section className="rounded-2xl border border-[#E0D9CD] bg-white p-3 sm:p-4" aria-label="Filtros de materiais">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="inline-flex w-full rounded-xl border border-[#E0D9CD] bg-[#FAF7F1] p-1 sm:w-auto" data-testid="material-status-filter">
            {STATUS_OPTS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setStatus(option.value)}
                className={`flex-1 rounded-lg px-3 py-2 text-xs font-semibold transition sm:flex-none ${status === option.value ? "bg-white text-[#D94A12] shadow-sm" : "text-[#6B655E] hover:text-[#1F1F1F]"}`}
                data-testid={`material-status-${option.value}`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-[#E0D9CD] bg-white px-3 py-2.5 lg:max-w-xl">
            <Search className="h-4 w-4 shrink-0 text-[#8A8378]" />
            <input
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder="Buscar por material, turma ou público…"
              className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-[#A59C90]"
              data-testid="materials-search"
            />
            {q && (
              <button type="button" onClick={() => setQ("")} className="grid h-7 w-7 place-items-center rounded-lg text-[#8A8378] hover:bg-[#FFF0E9] hover:text-[#D94A12]" aria-label="Limpar busca" data-testid="materials-search-clear">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </section>

      <div className="space-y-3">
        {filtered.map((material) => {
          const physical = material.material_type === "PHYSICAL";
          const scheduled = isScheduled(material.starts_at);
          const archived = material.status === "ARCHIVED";
          const MaterialIcon = physical ? Package : FileText;

          return (
            <article
              key={material.id}
              className="relative overflow-hidden rounded-2xl border border-[#E0D9CD] bg-white p-4 transition hover:border-[#D6CBBB] hover:shadow-[0_10px_30px_rgba(68,53,35,0.05)] sm:p-5"
              data-testid={`material-row-${material.id}`}
            >
              <span className={`absolute inset-y-0 left-0 w-1 ${physical ? "bg-[#F6A04D]" : "bg-[#F75A1B]"}`} aria-hidden="true" />

              <div className="flex items-start gap-3 pl-1">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#FFF0E9] text-[#F75A1B]">
                  <MaterialIcon className="h-5 w-5" />
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.13em] text-[#A59C90]">{physical ? "Material físico" : "Material online"}</p>
                      <h2 className="mt-0.5 font-display text-lg font-bold leading-tight text-[#111111] sm:text-xl">{material.title}</h2>
                    </div>
                    <MaterialRowMenu
                      material={material}
                      canManage={canManage}
                      busy={isBusy(`archive:${material.id}`) || isBusy(`reactivate:${material.id}`)}
                      onEdit={() => setEditing(material)}
                      onArchive={() => run(`archive:${material.id}`, () => archive(material))}
                      onReactivate={() => run(`reactivate:${material.id}`, () => reactivate(material))}
                    />
                  </div>

                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <MaterialChip icon={physical ? Package : FileText} tone="orange">{physical ? "Físico" : "Online"}</MaterialChip>
                    {archived && <MaterialChip icon={Archive}>Arquivado</MaterialChip>}
                    {scheduled && <MaterialChip icon={Clock3} tone="warning">Agendado</MaterialChip>}
                    {!material.visible_to_students && <MaterialChip icon={EyeOff}>Oculto para alunos</MaterialChip>}
                  </div>

                  {material.description && (
                    <p className="mt-3 max-w-3xl text-sm leading-relaxed text-[#5F574D]">{material.description}</p>
                  )}

                  <div className="mt-4 grid gap-2 border-t border-[#EFE7DC] pt-3 text-xs text-[#6B655E] sm:grid-cols-2 lg:grid-cols-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <Users className="h-3.5 w-3.5 shrink-0 text-[#F75A1B]" />
                      <span className="truncate"><strong className="font-semibold text-[#4A4640]">Turma:</strong> {classGroupMap[material.class_group_id] || "—"}</span>
                    </div>
                    <div className="flex min-w-0 items-center gap-2" data-testid={`material-audience-${material.id}`}>
                      <Users className="h-3.5 w-3.5 shrink-0 text-[#8A8378]" />
                      <span className="truncate"><strong className="font-semibold text-[#4A4640]">Público:</strong> {materialAudienceText(material)}</span>
                    </div>
                    {material.starts_at && (
                      <div className="flex min-w-0 items-center gap-2">
                        <CalendarClock className="h-3.5 w-3.5 shrink-0 text-[#8A8378]" />
                        <span className="truncate">Disponível a partir de {fmtDateTime(material.starts_at)}</span>
                      </div>
                    )}
                  </div>

                  {physical && material.delivery_note && (
                    <div className="mt-3 rounded-xl bg-[#FAF7F1] px-3 py-2.5 text-xs leading-relaxed text-[#5F574D]">
                      <span className="font-semibold text-[#4A4640]">Entrega:</span> {material.delivery_note}
                    </div>
                  )}

                  {!physical && material.link_url && (
                    <div className="mt-3">
                      <a
                        href={material.link_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-lg text-xs font-semibold text-[#F75A1B] hover:text-[#D94A12] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/25"
                        data-testid={`open-material-${material.id}`}
                      >
                        <ExternalLink className="h-3.5 w-3.5" /> Abrir material
                      </a>
                    </div>
                  )}

                  {physical && canManage && material.status === "ACTIVE" && <MaterialReceipts material={material} />}
                </div>
              </div>
            </article>
          );
        })}

        {!loading && items.length === 0 && (
          <section className="rounded-2xl border border-[#E0D9CD] bg-white">
            <EmptyState icon={FileText} title="Nenhum material nesta visão" description="Crie um material ou altere o filtro para consultar itens arquivados." />
          </section>
        )}
        {!loading && items.length > 0 && filtered.length === 0 && (
          <section className="rounded-2xl border border-[#E0D9CD] bg-white">
            <EmptyState icon={Search} title="Nenhum material encontrado" description="Tente outro termo ou limpe a busca para ver todos os materiais desta visão." />
          </section>
        )}
      </div>

      {editing && (
        <MaterialModal
          material={editing === "new" ? null : editing}
          classGroups={classGroups}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function MaterialReceipts({ material }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get(`/materials/${material.id}/receipts`).then((response) => setData(response.data)).catch(() => {});
  useEffect(() => { void load(); }, [material.id]);
  useVisibleRefresh(() => load(), { intervalMs: 0 });
  const { isBusy, run } = useBusyKeys();

  const mark = async (studentId, received) => {
    try {
      await api.post(`/materials/${material.id}/receipt`, { student_id: studentId, received });
      load();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  if (!data) return null;

  const total = Number(data.total_active || 0);
  const received = Number(data.total_received || 0);
  const pending = Number(data.total_pending || 0);
  const progress = total > 0 ? Math.min(100, Math.round((received / total) * 100)) : 0;

  return (
    <div className="mt-4 overflow-hidden rounded-xl border border-[#E4DED4] bg-[#FCFAF6]" data-testid={`receipts-${material.id}`}>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center gap-3 px-3.5 py-3 text-left transition hover:bg-white"
        data-testid={`receipts-toggle-${material.id}`}
      >
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[#E6F4EA] text-[#2F855A]">
          <CheckCircle2 className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs font-semibold text-[#3F3A34]">Recebimento do material</p>
            <p className="shrink-0 text-[11px] font-semibold text-[#2F7A45]">{received}/{total} recebidos</p>
          </div>
          <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[#E8E2D8]">
            <div className="h-full rounded-full bg-[#2F855A] transition-[width]" style={{ width: `${progress}%` }} />
          </div>
          <p className="mt-1 text-[10px] text-[#8A8378]">{pending} pendente(s)</p>
        </div>
        <ChevronDown className={`h-4 w-4 shrink-0 text-[#8A8378] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-[#E7E0D6] bg-white px-3.5 py-2">
          {[...data.pending, ...data.received].map((student) => (
            <div key={student.student_id} className="flex items-center justify-between gap-3 border-b border-[#F0EBE3] py-2.5 last:border-b-0" data-testid={`receipt-row-${student.student_id}`}>
              <div className="min-w-0">
                <p className={`truncate text-xs font-medium ${student.received ? "text-[#2F7A45]" : "text-[#4A4640]"}`}>{student.full_name}</p>
                {student.received && student.received_at && <p className="mt-0.5 text-[10px] text-[#8A8378]">Recebido em {fmtDateTime(student.received_at)}</p>}
              </div>
              {student.received ? (
                <button
                  type="button"
                  disabled={isBusy(`unset:${student.student_id}`)}
                  onClick={() => run(`unset:${student.student_id}`, () => mark(student.student_id, false))}
                  className="shrink-0 rounded-lg px-2 py-1 text-[11px] font-medium text-[#6B655E] hover:bg-[#F6F3EE] disabled:opacity-50"
                  data-testid={`receipt-unset-${material.id}-${student.student_id}`}
                >
                  Desfazer
                </button>
              ) : (
                <button
                  type="button"
                  disabled={isBusy(`set:${student.student_id}`)}
                  onClick={() => run(`set:${student.student_id}`, () => mark(student.student_id, true))}
                  className="shrink-0 rounded-lg bg-[#E6F4EA] px-2.5 py-1.5 text-[11px] font-semibold text-[#2F855A] transition hover:brightness-95 disabled:opacity-50"
                  data-testid={`receipt-set-${material.id}-${student.student_id}`}
                >
                  {isBusy(`set:${student.student_id}`) ? "Salvando…" : "Marcar recebido"}
                </button>
              )}
            </div>
          ))}
          {total === 0 && <p className="py-3 text-xs text-[#6B655E]">Nenhum aluno ativo na turma.</p>}
        </div>
      )}
    </div>
  );
}

function MaterialModal({ material, classGroups, onClose, onSaved }) {
  const isEdit = Boolean(material);
  const [cells, setCells] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [draggingFile, setDraggingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [form, setForm] = useState({
    title: material?.title || "",
    description: material?.description || "",
    link_url: material?.link_url || "",
    material_type: material?.material_type || "ONLINE",
    delivery_note: material?.delivery_note || "",
    visible_to_students: material?.visible_to_students ?? true,
    starts_at: toLocalInput(material?.starts_at),
    class_group_id: material?.class_group_id || classGroups[0]?.id || "",
    module_id: material?.module_id || "",
    lesson_id: material?.lesson_id || "",
    audience_type: material?.audience_type || "CLASS",
    audience_cell: material?.audience_cell || "",
    audience_student_ids: material?.audience_student_ids || [],
  });
  const [busy, setBusy] = useState(false);
  const isPhysical = form.material_type === "PHYSICAL";
  const inputClass = "w-full rounded-xl border border-[#E0D9CD] bg-white px-3 py-2.5 text-sm outline-none transition placeholder:text-[#B0A79B] focus:border-[#F75A1B] focus:ring-2 focus:ring-[#F75A1B]/15";

  useEffect(() => {
    api.get("/cells").then(({ data }) => setCells(data)).catch(() => setCells([]));
    api.get("/students").then(({ data }) => setStudents(data)).catch(() => setStudents([]));
  }, []);

  const classStudents = students.filter((student) => student.class_group_id === form.class_group_id);
  const toggleStudent = (id) => {
    setForm((current) => ({
      ...current,
      audience_student_ids: current.audience_student_ids.includes(id)
        ? current.audience_student_ids.filter((studentId) => studentId !== id)
        : [...current.audience_student_ids, id],
    }));
  };

  const chooseFile = (file) => {
    const error = validateFileForUpload(file);
    if (error) {
      toast.error(error);
      return;
    }
    setSelectedFile(file);
    setUploadProgress(0);
  };

  const setMaterialType = (value) => {
    setForm((current) => ({ ...current, material_type: value }));
    if (value === "PHYSICAL") {
      setSelectedFile(null);
      setUploadProgress(0);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.class_group_id) return toast.error("Selecione a turma.");
    if (form.audience_type === "CELL" && !form.audience_cell) return toast.error("Selecione a célula do público-alvo.");
    if (form.audience_type === "STUDENTS" && form.audience_student_ids.length === 0) return toast.error("Selecione ao menos um aluno.");
    if (!isPhysical && !selectedFile && !form.link_url) return toast.error("Selecione o arquivo do material.");

    setBusy(true);
    setUploadProgress(0);
    try {
      let linkUrl = isPhysical ? "" : form.link_url;

      if (!isPhysical && selectedFile) {
        const uploadBody = new FormData();
        uploadBody.append("file", selectedFile);
        const { data } = await api.post("/materials/upload", uploadBody, {
          onUploadProgress: (progressEvent) => {
            if (!progressEvent.total) return;
            setUploadProgress(Math.min(100, Math.round((progressEvent.loaded / progressEvent.total) * 100)));
          },
        });
        linkUrl = data.url;
        setForm((current) => ({ ...current, link_url: data.url }));
      }

      const payload = {
        title: form.title,
        description: form.description,
        link_url: linkUrl,
        material_type: form.material_type,
        delivery_note: isPhysical ? form.delivery_note : "",
        visible_to_students: form.visible_to_students,
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
        class_group_id: form.class_group_id,
        module_id: form.module_id || null,
        lesson_id: form.lesson_id || null,
        audience_type: form.audience_type,
        audience_cell: form.audience_type === "CELL" ? form.audience_cell : null,
        audience_student_ids: form.audience_type === "STUDENTS" ? form.audience_student_ids : [],
      };

      if (isEdit) await api.put(`/materials/${material.id}`, payload);
      else await api.post("/materials", payload);

      toast.success(isEdit ? "Material atualizado!" : "Material criado!");
      onSaved();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setBusy(false);
    }
  };

  const existingOnlineFile = !isPhysical && Boolean(form.link_url) && !selectedFile;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3 sm:p-5" onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-[22px] border border-[#E0D9CD] bg-[#FBF8F2] shadow-[0_24px_80px_rgba(38,27,19,.24)]" onClick={(event) => event.stopPropagation()} data-testid="material-modal">
        <header className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-[#E5DDD2] bg-white/95 px-5 py-4 backdrop-blur sm:px-6">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#F75A1B]">Conteúdo acadêmico</p>
            <h2 className="mt-0.5 font-display text-xl font-bold">{isEdit ? "Editar material" : "Novo material"}</h2>
            <p className="mt-1 text-xs text-[#6B655E]">Defina o arquivo, vínculo acadêmico, público e disponibilidade.</p>
          </div>
          <button type="button" onClick={onClose} className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-[#6B655E] transition hover:bg-[#FFF0E9] hover:text-[#D94A12]" aria-label="Fechar">
            <X className="h-4 w-4" />
          </button>
        </header>

        <form onSubmit={submit} className="space-y-4 p-4 sm:p-6">
          <section className="rounded-2xl border border-[#E0D9CD] bg-white p-4 sm:p-5">
            <div className="mb-4">
              <h3 className="font-display text-base font-bold">Conteúdo</h3>
              <p className="mt-1 text-xs text-[#6B655E]">Escolha o tipo e descreva o material que será disponibilizado.</p>
            </div>

            <div>
              <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Tipo de material</label>
              <div className="grid grid-cols-2 gap-2 rounded-xl bg-[#F8F4ED] p-1" data-testid="material-type-toggle">
                {[
                  ["ONLINE", "Online", FileText],
                  ["PHYSICAL", "Físico", Package],
                ].map(([value, label, Icon]) => (
                  <button
                    type="button"
                    key={value}
                    onClick={() => setMaterialType(value)}
                    className={`inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-semibold transition ${form.material_type === value ? "border-[#F75A1B] bg-white text-[#D94A12] shadow-sm" : "border-transparent text-[#6B655E] hover:text-[#1F1F1F]"}`}
                    data-testid={`material-type-${value.toLowerCase()}`}
                  >
                    <Icon className="h-4 w-4" /> {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Título</label>
                <input required value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} className={inputClass} data-testid="material-title" />
              </div>

              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Descrição</label>
                <textarea value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} className={inputClass} rows={3} />
              </div>

              {!isPhysical ? (
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Arquivo do material</label>
                  <div
                    className={`rounded-2xl border-2 border-dashed p-4 transition ${draggingFile ? "border-[#F75A1B] bg-[#FFF7F2]" : "border-[#DDD4C7] bg-[#FCFAF6] hover:border-[#F75A1B]/55"}`}
                    onDragEnter={(event) => { event.preventDefault(); setDraggingFile(true); }}
                    onDragOver={(event) => { event.preventDefault(); setDraggingFile(true); }}
                    onDragLeave={(event) => { event.preventDefault(); setDraggingFile(false); }}
                    onDrop={(event) => {
                      event.preventDefault();
                      setDraggingFile(false);
                      const file = event.dataTransfer.files?.[0];
                      if (file) chooseFile(file);
                    }}
                    data-testid="material-file-dropzone"
                  >
                    {selectedFile ? (
                      <div className="flex items-center gap-3">
                        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#FFF0E9] text-[#F75A1B]">
                          <Paperclip className="h-5 w-5" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-[#302D29]">{selectedFile.name}</p>
                          <p className="mt-0.5 text-xs text-[#7B746B]">{formatFileSize(selectedFile.size)} · pronto para enviar</p>
                          {busy && uploadProgress > 0 && uploadProgress < 100 && (
                            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#E8E2D8]">
                              <div className="h-full rounded-full bg-[#F75A1B] transition-[width]" style={{ width: `${uploadProgress}%` }} />
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => { setSelectedFile(null); setUploadProgress(0); }}
                          className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-[#8A8378] transition hover:bg-[#FDECEA] hover:text-[#B74237] disabled:opacity-50"
                          aria-label="Remover arquivo selecionado"
                          data-testid="material-file-remove"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl px-4 py-5 text-center" data-testid="material-file-picker">
                        <span className="grid h-11 w-11 place-items-center rounded-xl bg-[#FFF0E9] text-[#F75A1B]">
                          <Upload className="h-5 w-5" />
                        </span>
                        <span className="mt-3 text-sm font-semibold text-[#302D29]">{existingOnlineFile ? "Substituir arquivo" : "Selecionar arquivo"}</span>
                        <span className="mt-1 max-w-md text-xs leading-relaxed text-[#7B746B]">
                          {existingOnlineFile
                            ? "O material atual continua vinculado. Escolha outro arquivo somente se quiser substituí-lo."
                            : "Arraste o arquivo aqui ou clique para escolher no computador."}
                        </span>
                        <span className="mt-2 text-[11px] font-medium text-[#9B9287]">PDF, Word, Excel, PowerPoint, JPG, PNG ou WebP · até 50 MB</span>
                        <input
                          type="file"
                          accept={MATERIAL_FILE_ACCEPT}
                          className="sr-only"
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            if (file) chooseFile(file);
                            event.target.value = "";
                          }}
                          data-testid="material-file-input"
                        />
                      </label>
                    )}
                  </div>
                  {existingOnlineFile && (
                    <div className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-[#EEF8F2] px-2.5 py-1.5 text-[11px] font-semibold text-[#2F7A45]">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Arquivo atual preservado
                    </div>
                  )}
                </div>
              ) : (
                <div className="sm:col-span-2">
                  <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Observação de entrega</label>
                  <input value={form.delivery_note} onChange={(event) => setForm({ ...form, delivery_note: event.target.value })} className={inputClass} placeholder="Ex.: retirar com a secretaria após a aula" data-testid="material-delivery-note" />
                </div>
              )}
            </div>
          </section>

          <section className="rounded-2xl border border-[#E0D9CD] bg-white p-4 sm:p-5">
            <div className="mb-4">
              <h3 className="font-display text-base font-bold">Vínculo acadêmico</h3>
              <p className="mt-1 text-xs text-[#6B655E]">Associe o material à turma e, quando necessário, a um módulo ou aula específica.</p>
            </div>
            <RelationSelects
              classGroups={classGroups}
              value={{ class_group_id: form.class_group_id, module_id: form.module_id, lesson_id: form.lesson_id }}
              onChange={(value) => setForm({ ...form, ...value })}
              prefix="material"
            />
          </section>

          <section className="rounded-2xl border border-[#E0D9CD] bg-white p-4 sm:p-5">
            <div className="mb-4">
              <h3 className="font-display text-base font-bold">Público e disponibilidade</h3>
              <p className="mt-1 text-xs text-[#6B655E]">Controle quem recebe o material e a partir de quando ele aparece.</p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Público-alvo</label>
                <DoxaSelect
                  value={form.audience_type}
                  onValueChange={(value) => setForm({ ...form, audience_type: value })}
                  options={[
                    { value: "CLASS", label: "Toda a turma" },
                    { value: "CELL", label: "Célula específica" },
                    { value: "STUDENTS", label: "Alunos específicos" },
                  ]}
                  className="w-full"
                  testId="material-audience"
                  ariaLabel="Público-alvo"
                />
              </div>

              {form.audience_type === "CELL" && (
                <div>
                  <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Célula</label>
                  <DoxaSelect
                    value={form.audience_cell}
                    onValueChange={(value) => setForm({ ...form, audience_cell: value })}
                    options={[{ value: "", label: "Selecione a célula…" }, ...cells.map((cell) => ({ value: cell.name, label: cell.name }))]}
                    className="w-full"
                    testId="material-cell"
                    ariaLabel="Célula do público-alvo"
                  />
                </div>
              )}

              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-xs font-semibold text-[#4A4640]">Disponível a partir de</label>
                <input type="datetime-local" value={form.starts_at} onChange={(event) => setForm({ ...form, starts_at: event.target.value })} className={inputClass} data-testid="material-starts" />
              </div>
            </div>

            {isPhysical && form.audience_type !== "CLASS" && (
              <p className="mt-3 rounded-xl bg-[#FFF7E8] px-3 py-2 text-xs leading-relaxed text-[#8A5A16]">Somente os alunos-alvo verão e confirmarão o recebimento deste material físico.</p>
            )}

            {form.audience_type === "STUDENTS" && (
              <div className="mt-4 max-h-52 overflow-y-auto rounded-xl border border-[#E0D9CD] bg-[#FAF7F1] p-2" data-testid="material-students">
                {classStudents.map((student) => {
                  const checked = form.audience_student_ids.includes(student.id);
                  return (
                    <label key={student.id} className="flex cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2 text-sm transition hover:bg-white">
                      <input type="checkbox" checked={checked} onChange={() => toggleStudent(student.id)} className="h-4 w-4 accent-[#F75A1B]" data-testid={`material-student-${student.id}`} />
                      <span className="min-w-0 flex-1 truncate">{student.full_name}</span>
                      {checked && <CheckCircle2 className="h-4 w-4 shrink-0 text-[#2F855A]" />}
                    </label>
                  );
                })}
                {classStudents.length === 0 && <p className="px-2 py-4 text-xs text-[#6B655E]">Nenhum aluno ativo nesta turma. Selecione a turma primeiro.</p>}
              </div>
            )}

            <label className="mt-4 flex cursor-pointer items-center justify-between gap-4 rounded-xl border border-[#E0D9CD] bg-[#FAF7F1] px-3.5 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${form.visible_to_students ? "bg-[#E6F4EA] text-[#2F855A]" : "bg-[#F1ECE5] text-[#8A8378]"}`}>
                  {form.visible_to_students ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-[#3F3A34]">Visível para alunos</p>
                  <p className="mt-0.5 text-[10px] text-[#8A8378]">Desative para manter o material somente na administração.</p>
                </div>
              </div>
              <input type="checkbox" checked={form.visible_to_students} onChange={(event) => setForm({ ...form, visible_to_students: event.target.checked })} className="h-4 w-4 shrink-0 accent-[#F75A1B]" data-testid="material-visible" />
            </label>
          </section>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button type="button" onClick={onClose} disabled={busy} className="rounded-xl border border-[#E0D9CD] bg-white px-4 py-2.5 text-sm font-semibold text-[#5F574D] transition hover:bg-[#F8F4ED] disabled:opacity-50">Cancelar</button>
            <button type="submit" disabled={busy} className="doxa-btn-primary inline-flex items-center justify-center gap-2 px-5 py-2.5" data-testid="material-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {busy ? (!isPhysical && selectedFile ? "Enviando e salvando…" : "Salvando…") : isEdit ? "Salvar alterações" : "Criar material"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
